import { useState, type KeyboardEvent } from "react"
import { useIsHotelEdition } from "@/lib/hotelEdition"
import { Send } from "lucide-react"
import { LinaChatModal } from "@/components/dashboard/LinaChatModal"
import linaImage from "@assets/lina desk_1764231656647.png"

// Lina speaks as a briefing, not as a chat box: short paragraphs, an optional market
// signal, and the question input below. The weekly-briefing slice swaps the paragraphs
// for the stored per-week text; until then the caller passes the dashboard briefing.

interface LinaBriefingPanelProps {
  paragraphs: string[]
  signal?: string | null
  companyId: string
  userId: string
  userName?: string
  profileMissing?: boolean
}

export function LinaBriefingPanel({ paragraphs, signal, companyId, userId, userName, profileMissing }: LinaBriefingPanelProps) {
  const isHotel = useIsHotelEdition(); // Hotels: Sie-Form placeholders (Mike 2026-10-10)
  const [isChatOpen, setIsChatOpen] = useState(false)
  const [inputValue, setInputValue] = useState("")
  const [initialMessage, setInitialMessage] = useState<string | undefined>()
  const chatDisabled = !companyId || !userId

  const openChat = (message?: string) => {
    if (chatDisabled) return
    setInitialMessage(message)
    setIsChatOpen(true)
    setInputValue("")
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault()
      openChat(inputValue.trim())
    }
  }

  return (
    <>
      <section className="flex flex-col rounded-xl bg-zinc-900 p-4 text-zinc-100 sm:p-5 dark:bg-zinc-950" data-testid="cockpit-lina">
        <div className="mb-3.5 flex items-center gap-3">
          <img src={linaImage} alt="" className="h-11 w-11 rounded-xl object-cover" />
          <div>
            <p className="text-sm font-semibold text-white">Linas Wochen-Briefing</p>
            <p className="text-[11px] uppercase tracking-[0.12em] text-zinc-400">The Strategist</p>
          </div>
        </div>

        <div className="space-y-2.5" data-testid="lina-paragraphs">
          {paragraphs.map((p, i) => (
            <p key={i} className="text-[13.5px] leading-relaxed text-zinc-200">
              {p}
            </p>
          ))}
        </div>

        {signal && (
          <p className="mt-3 inline-flex items-center gap-2 self-start rounded-full bg-zinc-800 px-2.5 py-1.5 text-xs text-zinc-300" data-testid="lina-signal">
            <span className="h-2 w-2 rounded-full bg-primary" aria-hidden="true" />
            {signal}
          </p>
        )}

        <div className="mt-4 flex gap-2 border-t border-zinc-800 pt-3.5">
          <input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={isHotel ? "Fragen Sie Lina etwas, zum Beispiel: Warum kommt am Mittwoch kein Beitrag?" : "Frag Lina etwas, zum Beispiel: Warum kein Post am Mittwoch?"}
            disabled={chatDisabled}
            className="min-w-0 flex-1 rounded-md border border-zinc-700 bg-zinc-800 px-2.5 py-2 text-[13px] text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
            data-testid="input-lina-question"
          />
          <button
            type="button"
            onClick={() => (inputValue.trim() ? openChat(inputValue.trim()) : openChat())}
            disabled={chatDisabled}
            className="rounded-md border border-zinc-700 px-3 text-white transition-colors hover:bg-zinc-800 disabled:opacity-60"
            aria-label="Frage an Lina senden"
            data-testid="button-send-question"
          >
            <Send className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        {chatDisabled && (
          <p className="mt-2 text-xs text-zinc-400">
            {profileMissing
              ? "Lina ist startklar, sobald Sie Ihr Firmenprofil angelegt haben."
              : "Lina ist verfügbar, sobald Ihr Firmenprofil geladen ist."}
          </p>
        )}
      </section>

      <LinaChatModal
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        initialMessage={initialMessage}
        companyId={companyId}
        userId={userId}
        userName={userName}
      />
    </>
  )
}
